import { useEffect, useRef } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { components } from "@contracts/generated/editor-service";

import { useApiClient } from "./ApiContext";
import { unwrap } from "./client";
import { queryKeys } from "./hooks";

/**
 * A Lab run's final clinical summary, read live through the Editor Service
 * (`…/preview-runs/{previewRunRef}/final-summary`). Synthetic preview output;
 * nothing is kept in the browser beyond the query cache. Liv creates the
 * summary when the conversation ends; `…/final-summary/end` ends it early.
 */
export type PreviewFinalSummary = components["schemas"]["PreviewFinalSummary"];
export type FinalSummaryContent = components["schemas"]["FinalSummaryContent"];

export const FINAL_SUMMARY_POLL_MS = 5_000;

const finalSummaryKey = (projectId: string, previewRunRef: string) =>
  [...queryKeys.previewRun(projectId, previewRunRef), "final-summary"] as const;

/** `runEnded` flipping to true re-reads the status: Liv starts the summary when the run ends. */
export function usePreviewFinalSummaryQuery(
  projectId: string,
  previewRunRef: string,
  runEnded: boolean,
): UseQueryResult<PreviewFinalSummary> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const wasEnded = useRef(runEnded);
  useEffect(() => {
    if (runEnded && !wasEnded.current) {
      void queryClient.invalidateQueries({
        queryKey: finalSummaryKey(projectId, previewRunRef),
      });
    }
    wasEnded.current = runEnded;
  }, [runEnded, queryClient, projectId, previewRunRef]);
  return useQuery({
    queryKey: finalSummaryKey(projectId, previewRunRef),
    async queryFn() {
      return unwrap(
        await client.GET(
          "/v1/projects/{projectId}/preview-runs/{previewRunRef}/final-summary",
          { params: { path: { projectId, previewRunRef } } },
        ),
      );
    },
    enabled: projectId.length > 0 && previewRunRef.length > 0,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.status === "queued" ||
      query.state.data?.status === "processing"
        ? FINAL_SUMMARY_POLL_MS
        : false,
  });
}

export function useEndPreviewFinalSummaryMutation(
  projectId: string,
  previewRunRef: string,
): UseMutationResult<PreviewFinalSummary, unknown, void> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    async mutationFn() {
      return unwrap(
        await client.POST(
          "/v1/projects/{projectId}/preview-runs/{previewRunRef}/final-summary/end",
          { params: { path: { projectId, previewRunRef } } },
        ),
      );
    },
    onSuccess(summary) {
      queryClient.setQueryData(
        finalSummaryKey(projectId, previewRunRef),
        summary,
      );
    },
  });
}
